// =============================================================================
// app/src/lib/reciboDoc.ts — o recibo da venda e a notinha do entregador.
//
// Pedido do dono (20/08/2026): "sistema de emissão de nota para vendas, NÃO
// nota fiscal — é como um recibo. Se comprou na loja: o que comprou, nome,
// horário, atendente, escopo de nota padrão. Se comprou via entrega: uma
// notinha para o entregador ter noção do que vai ser entregue e SE FOI PAGO —
// nome, endereço, peça, pago ou não, método de pagamento (se não foi pago)."
//
// ---------------------------------------------------------------------------
// SÃO DOIS PAPÉIS DIFERENTES, PARA DUAS PESSOAS DIFERENTES
//
// O RECIBO é para o CLIENTE: prova do que ele levou e por quanto. Fala de
// peças, valores e garantia.
//
// A NOTINHA é para o ENTREGADOR, que está de moto e vai ler no semáforo. A
// primeira coisa que ele precisa saber não é o que tem na caixa: é SE COBRA.
// Por isso a tarja de pagamento vem antes de tudo, e o endereço vem antes das
// peças. Sai em duas vias: uma fica com o cliente, o canhoto volta assinado —
// é o que prova que a peça chegou quando o cliente disser que não chegou.
//
// NÃO É DOCUMENTO FISCAL, e o papel diz isso com todas as letras. Recibo que
// se parece com nota fiscal cria problema com o cliente e com o fisco; a
// frase no rodapé é a diferença entre um comprovante honesto e um documento
// que finge ser o que não é.
//
// Mesmas travas dos outros documentos (aVerDoc, encomendaDoc, diaDoc): todo
// campo do banco passa por `escaparHtml` — a janela nasce de about:blank e
// HERDA a origem do app, onde vive o token de quem imprime — e o documento
// declara CSP `script-src 'none'`.
// =============================================================================
import type { PaymentMethod, QuitacaoDaVenda } from '@core/types';
import { brl, dataHoraBR, dataSoDiaBR, qtd } from './formato';
import { escaparHtml, dataPorExtenso } from './aVerDoc';
import { enderecoEmLinha } from './orcamentoDoc';
import { negocio } from '@negocio';

export interface ItemDoRecibo {
  /** Id do sale_item — casa com `quitacao.itens_pagos` para marcar "(pago)"
   *  (065). Opcional: item sem id simplesmente nunca é marcado. */
  id?: string;
  descricao: string;
  quantidade: number;
  preco_unit: number;
  subtotal: number;
  /** 073 — o carro DESTA peça, já pronto para ler ("VolksWagen Kombi 1.4").
   *  Só vem preenchido quando a linha é de um carro DIFERENTE do carro da
   *  venda; senão o papel repetiria o mesmo carro em toda linha. */
  veiculo?: string | null;
}

export interface VendaDoRecibo {
  codigo: string;
  vendida_em: string;
  vendedor: string | null;
  cliente: string | null;
  total: number;
  desconto: number;
  forma_pagamento: PaymentMethod | null;
  /** As PARTES do pagamento dividido (075) — só quando a forma é 'misto'. O
   *  papel diz quanto foi em cada uma; ausente, sai só "Misto". */
  pagamentos?: ReadonlyArray<{ forma_pagamento: PaymentMethod; valor: number }> | null;
  observacao: string | null;
  /** 073 — o carro da venda, pronto para ler. Ausente ou vazio = a venda não
   *  informou carro, e o papel não mostra a linha (decisão do dono: "se tiver
   *  carro, sai no recibo, se não, não"). */
  veiculo?: string | null;
  itens: ItemDoRecibo[];
  // --- A entrega (061). Ausente = venda de balcão.
  entrega?: boolean;
  entrega_endereco?: string | null;
  entrega_contato?: string | null;
  entrega_observacao?: string | null;
  entrega_forma_prevista?: PaymentMethod | null;
  entrega_troco_para?: number | null;
  /** A taxa de entrega (064). Soma no que o cliente paga e sai em linha
   *  própria: o cliente tem que ver por que pagou mais que as peças. */
  entrega_taxa?: number;
  /** O acerto da venda fiada (quitacao_da_venda, 25/08/2026). Ausente ou null
   *  = não sabemos / não há conta; o documento se comporta como antes, ou
   *  seja, MANDA COBRAR. A degradação é sempre para o lado seguro. */
  quitacao?: QuitacaoDaVenda | null;
}

/** O que o papel precisa dizer sobre o dinheiro desta venda.
 *
 *  Pedido do dono (25/08/2026): "o fiado deve virar venda completa quando
 *  recebermos, para conseguirmos emitir a nota". A venda continua fiada — foi
 *  assim que ela aconteceu — e o documento ganha o carimbo de quitação.
 *
 *  Aqui também mora um conserto: a tarja "A RECEBER" imprimia o TOTAL da
 *  venda. Numa fiada que usou vale "a ver", a dívida é menor que o total, e o
 *  papel mandava cobrar a mais. Agora ela sai pelo SALDO. */
/** O que o cliente paga: peças (já com desconto) + entrega. Derivado aqui e
 *  não recebido pronto — duas cópias do mesmo número no mesmo objeto é a
 *  regra 4 voltando pela porta do TypeScript. */
export const totalCobrado = (v: VendaDoRecibo): number =>
  Number(v.total) + Number(v.entrega_taxa ?? 0);

/** O que a tela precisa ter em mãos para montar o papel. Só o que o documento
 *  usa — nada de aceitar a linha inteira do banco, que mudaria de forma a cada
 *  migração. */
export interface VendaParaPapel {
  codigo: string;
  vendida_em: string;
  vendedor: string | null;
  cliente: string | null;
  total: number | string;
  desconto: number | string | null;
  forma_pagamento: PaymentMethod | null;
  /** O embed sale_payments(forma_pagamento, valor) da lista (075). */
  sale_payments?: ReadonlyArray<{ forma_pagamento: PaymentMethod; valor: number | string }> | null;
  observacao: string | null;
  /** 073 — como está no banco: separado, e com a montadora no formato da FIPE
   *  ("VW - VolksWagen"). Quem monta a frase legível é vendaParaPapel. */
  veiculo_montadora?: string | null;
  veiculo_modelo?: string | null;
  veiculo_ano?: number | string | null;
  entrega?: boolean | null;
  entrega_endereco?: string | null;
  entrega_contato?: string | null;
  entrega_observacao?: string | null;
  entrega_forma_prevista?: PaymentMethod | null;
  entrega_troco_para?: number | string | null;
  entrega_taxa?: number | string | null;
}

export interface ItemParaPapel {
  id: string;
  descricao: string;
  quantidade: number | string;
  preco_unit: number | string;
  subtotal: number | string;
  /** 073 — o carro próprio da linha, como está no banco. */
  veiculo_montadora?: string | null;
  veiculo_modelo?: string | null;
}

/**
 * A venda do banco vira o objeto do papel.
 *
 * POR QUE ISTO É UMA FUNÇÃO, e não um objeto literal montado na tela
 * (08/09/2026): a montagem estava dentro do componente e ESQUECEU
 * `entrega_taxa`. O campo existe no banco desde a 064, aparece na tela e entra
 * no total_cobrado — mas o papel saía sem a linha da entrega E com o total das
 * peças, menor do que o cliente pagou. Quatro vendas reais saíram assim
 * (VD-000513, VD-000550, VD-001068 e VD-001141: R$ 70 impressos numa venda de
 * R$ 82).
 *
 * Campo esquecido em objeto literal é erro que o TypeScript não pega — os
 * campos da entrega são todos opcionais no tipo do documento, de propósito,
 * porque venda de balcão não tem nenhum. Numa função com teste, o esquecimento
 * fica visível.
 */
/**
 * O carro em UMA frase, pronta para o papel. Devolve null quando não há carro
 * — e é esse null que faz a linha do recibo sumir, em vez de sair "Veículo: —".
 *
 * A montadora vem da FIPE com a sigla na frente em várias marcas
 * ("VW - VolksWagen", "GM - Chevrolet"). A sigla serve para casar com o
 * catálogo, não para o cliente ler.
 */
export function textoDoCarro(
  montadora: string | null | undefined,
  modelo: string | null | undefined,
  ano: number | string | null | undefined,
): string | null {
  if (!montadora || !modelo) return null;
  const partes = montadora.split(' - ');
  const marca = (partes.length > 1 ? partes[partes.length - 1]! : montadora).trim();
  return [marca, modelo, ano ? String(ano) : null].filter(Boolean).join(' ');
}

export function vendaParaPapel(
  v: VendaParaPapel,
  itens: ItemParaPapel[],
  quitacao: QuitacaoDaVenda | null = null,
): VendaDoRecibo {
  return {
    codigo: v.codigo,
    vendida_em: v.vendida_em,
    vendedor: v.vendedor,
    cliente: v.cliente,
    total: Number(v.total),
    desconto: Number(v.desconto ?? 0),
    forma_pagamento: v.forma_pagamento,
    // 075: as partes viajam para o papel — o cliente que pagou em duas formas
    // quer ver as duas no recibo. `Number` porque o PostgREST manda numeric
    // como texto.
    pagamentos: (v.sale_payments ?? []).map((p) => ({
      forma_pagamento: p.forma_pagamento, valor: Number(p.valor),
    })),
    observacao: v.observacao,
    // 073: a frase legível nasce AQUI, num lugar só, com teste. A montadora
    // perde o prefixo de sigla da FIPE — "VW - VolksWagen Kombi" é como o
    // catálogo fala, não como o cliente lê.
    veiculo: textoDoCarro(v.veiculo_montadora, v.veiculo_modelo, v.veiculo_ano),
    itens: itens.map((i) => ({
      id: i.id,
      descricao: i.descricao,
      quantidade: Number(i.quantidade),
      preco_unit: Number(i.preco_unit),
      subtotal: Number(i.subtotal),
      // A linha só carimba carro quando é OUTRO carro (o banco guarda nulo
      // quando é o mesmo da venda), então repetir aqui não acontece.
      veiculo: textoDoCarro(i.veiculo_montadora, i.veiculo_modelo, null),
    })),
    entrega: v.entrega ?? undefined,
    entrega_endereco: v.entrega_endereco,
    entrega_contato: v.entrega_contato,
    entrega_observacao: v.entrega_observacao,
    entrega_forma_prevista: v.entrega_forma_prevista,
    entrega_troco_para: v.entrega_troco_para == null ? null : Number(v.entrega_troco_para),
    // A LINHA QUE FALTAVA. Sem ela o papel some com a taxa e cobra a menos.
    entrega_taxa: Number(v.entrega_taxa ?? 0),
    quitacao,
  };
}

export function situacaoDaVenda(v: VendaDoRecibo): {
  situacao: 'paga' | 'aberta' | 'parcial' | 'quitada' | 'perdoada';
  aCobrar: number;
  carimbo: string | null;
  extrato: string | null;
} {
  const total = totalCobrado(v);
  if (v.forma_pagamento !== 'fiado') {
    return { situacao: 'paga', aCobrar: 0, carimbo: null, extrato: null };
  }
  const q = v.quitacao;
  // Sem informação do acerto: é o comportamento de sempre — cobra.
  if (!q) return { situacao: 'aberta', aCobrar: total, carimbo: null, extrato: null };
  if (q.perdido_em) {
    return { situacao: 'perdoada', aCobrar: 0, carimbo: null, extrato: null };
  }

  const recs = q.recebimentos ?? [];
  const saldo = Number(q.saldo);
  // Desde a 065 a dívida também zera SEM dinheiro: desconto negociado, ou peça
  // devolvida. Exigir recebimento aqui deixaria essa venda fechada sem tarja
  // nenhuma — e a notinha mandaria o entregador cobrar o que já foi acertado.
  const semDinheiro = Number(q.desconto ?? 0) + Number(q.devolvido ?? 0);
  const quitada = saldo <= 0.005 && (recs.length > 0 || semDinheiro > 0);
  const dia = q.ultimo_acerto_em ? dataSoDiaBR(q.ultimo_acerto_em) : null;

  // O carimbo em uma frase, do jeito que a pessoa fala: um pagamento diz a
  // forma; vários na mesma forma dizem quantos; formas diferentes discriminam.
  let carimbo: string | null = null;
  if (quitada && dia) {
    const porForma = new Map<string, number>();
    for (const r of recs) porForma.set(r.forma, (porForma.get(r.forma) ?? 0) + Number(r.valor));
    const formas = [...porForma.entries()];
    if (recs.length === 1) {
      carimbo = `QUITADA EM ${dia} · ${nomeDaForma(recs[0]!.forma).toUpperCase()}`;
    } else if (formas.length === 1) {
      carimbo = `QUITADA EM ${dia} · ${recs.length} PAGAMENTOS EM ${nomeDaForma(formas[0]![0]).toUpperCase()}`;
    } else {
      carimbo = `QUITADA EM ${dia} · ${formas
        .map(([f, val]) => `${brl(val)} EM ${nomeDaForma(f).toUpperCase()}`)
        .join(' E ')}`;
    }
  }

  const extrato = recs.length > 0
    ? recs.map((r) => `${dataSoDiaBR(r.recebido_em).slice(0, 5)} ${brl(Number(r.valor))} ${nomeDaForma(r.forma)}`)
        .join(' · ')
    : null;

  if (quitada) return { situacao: 'quitada', aCobrar: 0, carimbo, extrato };
  if (recs.length > 0) return { situacao: 'parcial', aCobrar: saldo, carimbo: null, extrato };
  return { situacao: 'aberta', aCobrar: saldo, carimbo: null, extrato: null };
}

/** O nome que a forma de pagamento tem na boca das pessoas. */
const FORMA: Record<string, string> = {
  dinheiro: 'Dinheiro',
  pix: 'PIX',
  debito: 'Cartão de débito',
  credito: 'Cartão de crédito',
  transferencia: 'Transferência',
  boleto: 'Boleto',
  a_ver: 'Vale "a ver"',
  fiado: 'Fiado (a receber)',
  misto: 'Misto',
};
export const nomeDaForma = (f: string | null | undefined): string =>
  (f ? FORMA[f] ?? f.replace(/_/g, ' ') : 'não informada');

/**
 * A linha "Pagamento:" do recibo. Na venda DIVIDIDA (075) diz as partes —
 * "Misto — R$ 100,00 Dinheiro + R$ 200,00 Cartão de crédito" — porque o
 * cliente que pagou em duas formas confere as duas no papel. Nas outras, o
 * nome da forma, como sempre.
 */
export function descricaoDoPagamento(v: Pick<VendaDoRecibo, 'forma_pagamento' | 'pagamentos'>): string {
  const partes = v.pagamentos ?? [];
  if (v.forma_pagamento === 'misto' && partes.length > 0) {
    const texto = partes
      .map((p) => `${brl(Number(p.valor))} ${nomeDaForma(p.forma_pagamento)}`)
      .join(' + ');
    return `${nomeDaForma('misto')} — ${texto}`;
  }
  return nomeDaForma(v.forma_pagamento);
}

/**
 * Pago ou não pago — a pergunta que a notinha existe para responder.
 *
 * Derivado da forma de pagamento, nunca de uma coluna "pago": venda fiada é a
 * única que ainda deve dinheiro, e é ela que o entregador cobra. Guardar isso
 * numa coluna criaria uma segunda verdade que um dia discorda da primeira.
 */
export function estaPaga(v: VendaDoRecibo): boolean {
  return v.forma_pagamento !== 'fiado';
}

// O SÍMBOLO SAI EM PRETO NO PAPEL, e isso é decisão de operação (20/08/2026).
// Na tela e no site ele é o vermelho da marca (#D90429, regra 10). Aqui não:
// UM pixel colorido faz a HP jato de tinta trocar para o modo em cores, e a
// página que sai em segundos passa a levar quase um minuto — com o cliente
// esperando no balcão. O agente ainda manda `print-color-mode: monochrome` por
// IPP; este preto é o cinto além do suspensório (documento que a loja imprime
// fora do agente também sai rápido).
const S_LOGO = `<svg viewBox="0 0 738 502" width="34" style="vertical-align:middle" xmlns="http://www.w3.org/2000/svg"><path fill="#111111" d="M265.16 197.096L716.099 197.133C710.828 236.013 703.583 277.477 697.547 316.427L691.319 357.303C688.727 374.581 687.153 388.315 681.173 404.926C673.717 426.092 661.62 445.323 645.765 461.208C626.687 480.431 602.358 493.601 575.825 499.063C558.852 502.518 534.196 501.482 516.359 501.482L0 501.506C6.45146 483.313 20.0753 438.942 29.209 424.596C33.8706 417.227 39.9357 410.846 47.0599 405.817C68.5016 390.473 90.0878 393.236 114.993 393.246L467.663 393.298C480.383 393.309 491.548 393.616 505.058 391.956C536.745 388.419 573.091 341.625 550.341 312.787C542.231 302.501 520.482 305.103 508.558 305.102L240.273 305.049L265.16 197.096Z"/><path fill="#111111" d="M195.821 0.543453C213.607 -0.496412 240.032 0.274996 258.347 0.279316L737.602 0.30894L726.382 34.5152C715.525 67.7255 704.255 100.888 663.231 107.702C652.029 109.563 638.877 108.711 627.422 108.697L297.094 108.625C283.826 108.599 258.16 107.708 246.463 110.265C234.27 113.024 222.913 118.655 213.336 126.689C202.159 136.104 193.793 148.416 189.157 162.274C184.055 177.316 183.118 204.508 181.773 221.171L174.91 305.148L27.0847 305.094C27.9432 296.569 30.2866 284.133 31.7117 275.497L41.2102 218.07L50.7884 158.008C53.5367 140.681 55.7487 122.604 61.4157 106.006C68.0344 86.9096 78.1359 69.2054 91.208 53.7901C117.493 22.6886 155.205 3.49334 195.821 0.543453Z"/></svg>`;

const ESTILO = `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  /* Uma folha A4 (1123 px CSS) — o agente fotografa exatamente isso e corta o
     que passar. Por isso as listas de item têm teto. */
  body { font-family: Arial, Helvetica, sans-serif; color: #111; padding: 7mm; font-size: 12.5px; }
  .via { border: 2px solid #111; padding: 5mm; margin-bottom: 4mm; page-break-inside: avoid; }
  header { display: flex; justify-content: space-between; align-items: flex-start;
           border-bottom: 2px solid #111; padding-bottom: 2.5mm; margin-bottom: 3mm; }
  .marca .nome { font-weight: 900; font-style: italic; font-size: 18px; margin-left: 6px; }
  .marca .sub { font-size: 9px; letter-spacing: 2px; margin-left: 6px; color: #555; }
  .marca .loja { display: block; font-size: 9.5px; color: #555; margin-top: 1mm; }
  .doc { text-align: right; }
  .doc .titulo { display: block; font-size: 11px; letter-spacing: 2px; }
  .doc .codigo { display: block; font-weight: 900; font-size: 18px; font-family: monospace; }
  .doc .quando { display: block; font-size: 10px; color: #555; }
  .tarja { border: 3px solid #111; background: #111; color: #fff; text-align: center;
           font-weight: 900; font-size: 15px; letter-spacing: 2px; padding: 3mm; margin-bottom: 3mm; }
  .tarja.paga { background: #fff; color: #111; }
  .destino { border: 2px solid #111; padding: 3mm; margin-bottom: 3mm; }
  .destino .rot { font-size: 9.5px; letter-spacing: 1.5px; text-transform: uppercase; color: #555; }
  .destino .quem { font-size: 15px; font-weight: 900; }
  .destino .onde { font-size: 14px; font-weight: 700; margin-top: 1mm; }
  .destino .mais { font-size: 11.5px; margin-top: 1mm; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 2.5mm; }
  th { font-size: 9.5px; text-transform: uppercase; letter-spacing: 1px; color: #555;
       text-align: left; border-bottom: 1px solid #111; padding-bottom: 1mm; }
  td { padding: 1.2mm 0; border-bottom: 1px solid #e6e6e6; font-size: 12px; }
  .num { text-align: right; white-space: nowrap; }
  .total { display: flex; justify-content: space-between; align-items: baseline;
           background: #f2f2f2; padding: 2.5mm 3mm; margin-bottom: 2.5mm; }
  .total .rot { font-size: 11px; letter-spacing: 1px; text-transform: uppercase; }
  .total .num2 { font-size: 22px; font-weight: 900; }
  .linhas { font-size: 11.5px; margin-bottom: 2.5mm; }
  .termos { font-size: 9.5px; line-height: 1.45; color: #333; border-top: 1px solid #ddd;
            padding-top: 2mm; }
  .assina { margin-top: 8mm; }
  .assina span { display: block; border-top: 1px solid #111; padding-top: 1.5mm;
                 font-size: 10px; text-align: center; }
  .rodape { font-size: 9px; color: #777; text-align: right; margin-top: 2mm; }
  @media print { body { padding: 0; } }
`;

function cabecalho(v: VendaDoRecibo, titulo: string, reimpressao: boolean): string {
  const fones = [negocio.telefoneFixo, negocio.whatsapp].filter(Boolean).join(' · ');
  return `<header>
    <div class="marca">
      ${S_LOGO}<span class="nome">SERGINHO</span><span class="sub">AUTO PEÇAS</span>
      <span class="loja">
        ${escaparHtml(enderecoEmLinha())}${fones ? ` · ${escaparHtml(fones)}` : ''}
        ${negocio.cnpj ? `<br>CNPJ ${escaparHtml(negocio.cnpj)}` : ''}
      </span>
    </div>
    <div class="doc">
      <span class="titulo">${escaparHtml(titulo)}${reimpressao ? ' · 2ª VIA' : ''}</span>
      <span class="codigo">${escaparHtml(v.codigo)}</span>
      <span class="quando">${escaparHtml(dataHoraBR(v.vendida_em))}</span>
    </div>
  </header>`;
}

function tabelaDeItens(v: VendaDoRecibo, teto: number, comValores: boolean): string {
  const visiveis = v.itens.slice(0, teto);
  const resto = v.itens.length - visiveis.length;
  // As peças já pagas do fiado (065): o cliente que paga peça a peça leva no
  // papel quais já saíram da conta — é o extrato que ele guarda na oficina.
  const pagas = new Set(v.quitacao?.itens_pagos ?? []);
  return `<table>
    <tr>
      <th>Peça</th><th class="num">Qtd</th>
      ${comValores ? '<th class="num">Unitário</th><th class="num">Total</th>' : ''}
    </tr>
    ${visiveis.map((i) => `<tr>
      <td>${escaparHtml(i.descricao)}${i.id && pagas.has(i.id)
        ? ' <span style="color:#555;font-size:10px">(pago)</span>' : ''}${i.veiculo
        ? `<br><span style="color:#555;font-size:10px">${escaparHtml(i.veiculo)}</span>` : ''}</td>
      <td class="num">${escaparHtml(qtd(Number(i.quantidade)))}</td>
      ${comValores ? `<td class="num">${escaparHtml(brl(Number(i.preco_unit)))}</td>
      <td class="num">${escaparHtml(brl(Number(i.subtotal)))}</td>` : ''}
    </tr>`).join('')}
    ${resto > 0 ? `<tr><td colspan="${comValores ? 4 : 2}" style="color:#666">
      e mais ${resto} ${resto === 1 ? 'peça' : 'peças'} — a lista completa está no sistema
    </td></tr>` : ''}
  </table>`;
}

/** O escopo padrão que o dono pediu: o que este papel é, e o que não é. */
function termos(): string {
  return `<p class="termos">
    <strong>Este documento NÃO é documento fiscal</strong> — é o comprovante da
    compra para conferência do cliente. Guarde-o: ele é o que identifica a venda
    em caso de troca ou garantia.
    Troca de peça só com este comprovante, na embalagem original e sem uso.
    Peça elétrica não tem troca por arrependimento.
    A garantia é a do fabricante, contada da data acima.
  </p>`;
}

/** O recibo do balcão. Uma via (o cliente leva) — `vias: 2` sai com a da loja. */
export function montarRecibo(
  v: VendaDoRecibo, opcoes: { vias?: 1 | 2; reimpressao?: boolean } = {},
): string {
  const vias = opcoes.vias ?? 1;
  const reimpressao = opcoes.reimpressao ?? false;

  const sit = situacaoDaVenda(v);
  const via = (rotulo: string) => `<div class="via">
    ${cabecalho(v, 'RECIBO DE VENDA', reimpressao)}

    <div class="linhas">
      ${v.cliente ? `<div><strong>Cliente:</strong> ${escaparHtml(v.cliente)}</div>` : ''}
      ${v.veiculo ? `<div><strong>Veículo:</strong> ${escaparHtml(v.veiculo)}</div>` : ''}
      <div><strong>Atendeu:</strong> ${escaparHtml(v.vendedor ?? '—')}</div>
      <div><strong>Pagamento:</strong> ${escaparHtml(descricaoDoPagamento(v))}</div>
      ${v.observacao ? `<div><strong>Obs.:</strong> ${escaparHtml(v.observacao)}</div>` : ''}
    </div>

    ${tabelaDeItens(v, 16, true)}

    ${Number(v.desconto) > 0
      ? `<div class="linhas"><strong>Desconto:</strong> − ${escaparHtml(brl(Number(v.desconto)))}</div>`
      : ''}

    ${Number(v.entrega_taxa ?? 0) > 0
      ? `<div class="linhas"><strong>Entrega a domicílio:</strong> + ${escaparHtml(brl(Number(v.entrega_taxa)))}</div>`
      : ''}

    <div class="total">
      <span class="rot">Total ${sit.situacao === 'aberta' || sit.situacao === 'parcial' ? 'a pagar' : 'pago'}</span>
      <span class="num2">${escaparHtml(brl(totalCobrado(v)))}</span>
    </div>

    ${/* O que abateu a DÍVIDA depois da venda (065) — outro bicho que o
          `v.desconto` acima, que é o desconto dado no ato. Sem estas linhas a
          conta do papel não fecha: total 300, "já pago 150" e "A RECEBER 100"
          só faz sentido dizendo de onde vieram os outros 50. Peça devolvida e
          desconto negociado saem SEPARADOS — o cliente confere a devolução
          dele, que é outra conversa. */''}
    ${Number(v.quitacao?.devolvido ?? 0) > 0
      ? `<div class="linhas"><strong>Peças devolvidas, abatidas da dívida:</strong>
           − ${escaparHtml(brl(Number(v.quitacao!.devolvido)))}</div>`
      : ''}
    ${Number(v.quitacao?.desconto ?? 0) > 0
      ? `<div class="linhas"><strong>Desconto concedido no acerto:</strong>
           − ${escaparHtml(brl(Number(v.quitacao!.desconto)))}</div>`
      : ''}

    ${sit.carimbo
      ? `<p class="tarja paga">FOI FIADO · ${escaparHtml(sit.carimbo)}</p>
         ${sit.extrato ? `<p class="rodape">${escaparHtml(sit.extrato)}</p>` : ''}`
      : sit.situacao === 'quitada'
        // Quitada sem carimbo é a dívida fechada só no desconto: não há data
        // de acerto para carimbar, mas o papel não pode sair mudo — mudo, ele
        // seria lido como "ainda devo".
        ? '<p class="tarja paga">FOI FIADO · QUITADA</p>'
        : sit.aCobrar > 0
          ? `<p class="tarja">A RECEBER · ${escaparHtml(brl(sit.aCobrar))}</p>
             ${sit.extrato ? `<p class="rodape">já pago: ${escaparHtml(sit.extrato)}</p>` : ''}`
          : ''}

    ${termos()}
    <p class="rodape">${escaparHtml(rotulo)} · ${escaparHtml(v.codigo)} · Rio Grande/RS,
      ${escaparHtml(dataPorExtenso(v.vendida_em))}</p>
  </div>`;

  const corpo = vias === 2
    ? via('Via do cliente') + via('Via da loja')
    : via('Via do cliente');

  return `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy"
      content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; script-src 'none'; form-action 'none'">
<title>${escaparHtml(v.codigo)} — Recibo</title>
<style>${ESTILO}</style></head>
<body>${corpo}</body></html>`;
}

/**
 * A notinha da entrega, em duas vias.
 *
 * A ordem da folha é a ordem das perguntas do entregador: **cobro?** → **onde
 * é?** → **o que levo?**. Foi montada nessa ordem de propósito; um papel que
 * começa pela lista de peças obriga a procurar o que importa no semáforo.
 */
export function montarNotinhaDeEntrega(
  v: VendaDoRecibo, opcoes: { reimpressao?: boolean } = {},
): string {
  // O entregador precisa de UMA resposta: cobrar ou não, e quanto. Fiado
  // quitado no balcão ANTES de ele sair não se cobra de novo — cobrança em
  // duplicidade na porta do cliente é o pior desfecho desta rota.
  const sit = situacaoDaVenda(v);
  const paga = sit.aCobrar <= 0;
  const tarja = sit.situacao === 'quitada'
    ? `FOI FIADO · ${sit.carimbo ?? 'QUITADA'} — NÃO COBRAR`
    : sit.situacao === 'perdoada'
      ? 'FIADO PERDOADO — NÃO COBRAR'
      : paga
        ? `PAGO · ${nomeDaForma(v.forma_pagamento)} — NÃO COBRAR`
        : `COBRAR ${brl(sit.aCobrar)} · ${nomeDaForma(v.entrega_forma_prevista) === 'não informada'
            ? 'FORMA A COMBINAR' : nomeDaForma(v.entrega_forma_prevista).toUpperCase()}`;

  const via = (rotulo: string, canhoto: boolean) => `<div class="via">
    ${cabecalho(v, 'ENTREGA', opcoes.reimpressao ?? false)}

    <p class="tarja${paga ? ' paga' : ''}">${escaparHtml(tarja)}</p>

    ${!paga && v.entrega_troco_para
      ? `<p class="linhas"><strong>Levar troco para ${escaparHtml(brl(Number(v.entrega_troco_para)))}</strong>
         — troco de ${escaparHtml(brl(Number(v.entrega_troco_para) - sit.aCobrar))}.</p>`
      : ''}

    <div class="destino">
      <span class="rot">Entregar para</span>
      <div class="quem">${escaparHtml(v.cliente ?? 'Cliente não identificado')}</div>
      <div class="onde">${escaparHtml(v.entrega_endereco ?? 'endereço não informado')}</div>
      ${v.entrega_contato ? `<div class="mais">Telefone: ${escaparHtml(v.entrega_contato)}</div>` : ''}
      ${v.entrega_observacao ? `<div class="mais">${escaparHtml(v.entrega_observacao)}</div>` : ''}
    </div>

    ${tabelaDeItens(v, 12, false)}

    ${Number(v.desconto) > 0
      ? `<div class="linhas"><strong>Desconto:</strong> − ${escaparHtml(brl(Number(v.desconto)))}</div>`
      : ''}

    ${/* A TAXA APARECE AQUI TAMBÉM (08/09/2026). O total já a somava, mas sem a
          linha a conta não fecha para quem confere na porta: as peças somam 70,
          o papel cobra 82, e o cliente não sabe de onde vieram os 12. É a
          mesma linha do recibo, pelo mesmo motivo. */''}
    ${Number(v.entrega_taxa ?? 0) > 0
      ? `<div class="linhas"><strong>Entrega a domicílio:</strong> + ${escaparHtml(brl(Number(v.entrega_taxa)))}</div>`
      : ''}

    <div class="total">
      <span class="rot">Valor da venda</span>
      <span class="num2">${escaparHtml(brl(totalCobrado(v)))}</span>
    </div>

    <div class="linhas">
      <strong>Vendeu:</strong> ${escaparHtml(v.vendedor ?? '—')}
      ${v.observacao ? ` · <strong>Obs.:</strong> ${escaparHtml(v.observacao)}` : ''}
    </div>

    ${canhoto
      ? `<div class="assina"><span>Recebi as peças acima — assinatura do cliente e data</span></div>`
      : termos()}

    <p class="rodape">${escaparHtml(rotulo)} · ${escaparHtml(v.codigo)}</p>
  </div>`;

  return `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy"
      content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; script-src 'none'; form-action 'none'">
<title>${escaparHtml(v.codigo)} — Entrega</title>
<style>${ESTILO}</style></head>
<body>
  ${via('Via do cliente', false)}
  ${via('Canhoto da loja — volta assinado', true)}
</body></html>`;
}

/** Abre a janela do navegador com o documento pronto para imprimir. */
export function imprimirAqui(html: string): void {
  const janela = window.open('', '_blank', 'width=800,height=900');
  if (!janela) return;
  janela.document.write(html);
  janela.document.close();
  janela.focus();
  janela.print();
}
